import { ChangeDetectorRef, Component, EventEmitter, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { QuestionAnswer, TopicAnswers } from '@app/_models';
import { CourseService } from '@app/_services/course.service';
import { TopicShareService } from '@app/_services/topic.share.service';
import { AuthenticationService, UserService } from '@app/_services';
import { first } from 'rxjs/operators';
import { ZipCodeSelectorComponent } from '../zipcode-selector/zipcode-selector.component';
import { combineLatest } from 'rxjs';

@Component({ templateUrl: 'soi-section.component.html', selector: 'soi-section' })
export class SOISectionComponent {

    @ViewChild('zipCodeSelector', { static: false }) zipCodeSelector: ZipCodeSelectorComponent;

    loading = false;
    sectionId: any;
    topic: any;
    theme: string = 'light';
    soicontent: any;
    topicAnswers: TopicAnswers;
    assessmentId: number;
    assessmentAnswerId: number;
    zipCodes: any[] = [];
    zipCodesControlPresent: boolean = false;
    frontlineCommunityDataContentPresent: boolean = false;
    frontlineDataDistributionContentPresent: boolean = false;
    worksheetContentPresent: boolean = false;
    announcement: any;

    yesTitleActive: boolean;
    noTitleActive: boolean;

    eventEmitter: EventEmitter<any> = new EventEmitter<any>();

    constructor(private route: ActivatedRoute,
        private courseService: CourseService,
        private shareService: TopicShareService,
        private authService: AuthenticationService,
        private userService: UserService,
        private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit() {

        this.topicAnswers = new TopicAnswers();

        this.route.paramMap.subscribe(params => {
            this.sectionId = params.get('id');

            this.yesTitleActive = false;
            this.noTitleActive = false;

            this.getTopic();
        });
    }

    get nonAssessmentAnswers() {
        if (this.topicAnswers.answers)
            return this.topicAnswers.answers.filter(a => !a.assessmentId);
        return [];
    }

    getTopic() {
        this.courseService.getTopic(this.sectionId).pipe(first()).subscribe(data => {
            this.loading = false;

            this.topic = data.section;

            if (data.section.assessment) {
                this.assessmentId = data.section.assessment.id;
            }

            this.theme = data.theme.toLowerCase();

            let textContent = this.topic.contents.filter(c => c.discriminator == "HtmlText" || c.discriminator == "ImageAndText");
            this.announcement = this.topic.contents.find(c => c.discriminator == "Announcement");

            let allContent = this.topic.contents;

            this.soicontent = { textContent: textContent, allContent: allContent };

            this.topicAnswers = new TopicAnswers();
            this.topicAnswers.topicId = this.topic.id;


            this.frontlineCommunityDataContentPresent = this.topic.contents.filter(c => c.discriminator == "FrontlineCommunityDataContent").length > 0;
            this.frontlineDataDistributionContentPresent = this.topic.contents.filter(c => c.discriminator == "FrontlineDataDistributionContent").length > 0;

            this.worksheetContentPresent = this.topic.contents.filter(c => c.discriminator == "WorksheetContent").length > 0;
            this.zipCodesControlPresent = this.topic.contents.filter(c => c.discriminator == "ZipCodePickerContent").length > 0;

            if (this.zipCodesControlPresent) {
                this.changeDetectorRef.detectChanges();
                this.zipCodeSelector.eventEmitter.subscribe(zipCodeData => {
                    console.log('zipcode emitter!');
                    if (zipCodeData && this.zipCodes.filter(x => x.zipCode == zipCodeData.zipCode).length == 0) {
                        this.zipCodes.push(zipCodeData);
                    }

                    this.eventEmitter.emit(this.zipCodes);
                });
            }

            let assessmentAnswer = data.answers.filter(a => a.assessment)[0];
            if (assessmentAnswer) {
                this.assessmentAnswerId = assessmentAnswer.answerId;
                this.markAssessmentAnswerAsActive(assessmentAnswer.answer, assessmentAnswer.assessment.id);
            }

            this.topic.assessments.forEach(a => {
                a.yesTitleActive = false;
                a.noTitleActive = false;

                let answerByAssessment = data.answers.filter(x => x.assessment && x.assessment.id == a.id)[0];

                if (answerByAssessment) {

                    a.reflection = answerByAssessment.reflection;

                    if (answerByAssessment.answer && answerByAssessment.answer == "Yes")
                        a.yesTitleActive = true;

                    if (answerByAssessment.answer && answerByAssessment.answer == "No")
                        a.noTitleActive = true;
                }
            });

            this.topicAnswers.answers = this.topic.questions.map(q => {

                var questionAnswer = new QuestionAnswer();
                questionAnswer.questionText = q.questionText;
                questionAnswer.placeholder = q.placeholder;
                questionAnswer.questionId = q.id;

                var answerResult = data.answers.find(a => {
                    return a.question.id == q.id;
                });

                if (answerResult) {
                    questionAnswer.answer = answerResult.answer;
                    questionAnswer.answerId = answerResult.answerId;
                } else {
                    questionAnswer.answerId = 0;
                }

                return questionAnswer;
            });
            this.shareService.emitter.emit(this.isValidSection());
        });
    }

    markAssessmentAnswerAsActive(assessmentAnswer, assessmentId) {

        let assessmentAnswered = this.topic.assessments.filter(a => a.id == assessmentId)[0];

        if (assessmentAnswer == assessmentAnswered.yesText) {
            assessmentAnswered.yesTitleActive = true;
            assessmentAnswered.noTitleActive = false;
        }

        if (assessmentAnswer == assessmentAnswered.noText) {
            assessmentAnswered.yesTitleActive = false;
            assessmentAnswered.noTitleActive = true;
        }
    }

    assessmentAnswer(answerText, assessmentId, isReflection: boolean = false) {

        this.markAssessmentAnswerAsActive(answerText, assessmentId);

        var answerAssessment = this.topicAnswers.answers.find(a => a.assessmentId > 0);

        if (answerAssessment) {
            if (isReflection)
                answerAssessment.reflection = answerText;
            else
                answerAssessment.answer = answerText;
        } else {
            var questionAnswer = new QuestionAnswer();
            questionAnswer.assessmentId = assessmentId;

            if (isReflection)
                questionAnswer.reflection = answerText;
            else
                questionAnswer.answer = answerText;

            questionAnswer.answerId = this.assessmentAnswerId;
            this.topicAnswers.answers.push(questionAnswer);
        }

        this.shareService.emitter.emit(this.isValidSection());
    }

    answerChanged(event, answerId, index, isReflection: boolean = false) {
        if (event.isAssessment) {
            let currentAssessment = this.topic.assessments.find(a => a.id == answerId);
            this.assessmentAnswer(event.answer.target.value, currentAssessment.id, isReflection);
        } else {
            if (answerId == 0) {
                this.topicAnswers.answers[index].answer = event.answer.target.value;
            } else {
                this.topicAnswers.answers.find(a => a.answerId == answerId).answer = event.answer.target.value;
            }
        }

        this.shareService.emitter.emit(this.isValidSection());
    }


    deleteZipCode(zipCode) {
        for (let i = 0; i < this.zipCodes.length; i++) {
            var data = this.zipCodes[i];
            if (data.zipCode == zipCode) {
                this.zipCodes.splice(i, 1);
            }
        }
    }

    isValidSection() {

        if (this.topicAnswers.answers) {

            for (var i = 0; i < this.topicAnswers.answers.length; i++) {

                if (this.topicAnswers.answers[i]) {
                    if (this.topicAnswers.answers[i].answer == undefined || this.topicAnswers.answers[i].answer.trim() == "") {
                        return false;
                    }
                } else {
                    return false;
                }
            }

            return true;
        }
        return true;
    }

    save() {
        let addZipCodesTask = null;
        if (this.zipCodesControlPresent && this.zipCodes.length > 0) {
            addZipCodesTask = this.userService.addCompanyZipCodes(this.authService.currentUserValue.id, this.zipCodes);
        }

        if (this.topicAnswers && this.topicAnswers.answers && this.topicAnswers.answers.length > 0)
            return addZipCodesTask
                ? combineLatest([addZipCodesTask, this.courseService.postAnswers(this.sectionId, this.topicAnswers)])
                : this.courseService.postAnswers(this.sectionId, this.topicAnswers);
        else
            return addZipCodesTask
                ? combineLatest([addZipCodesTask, this.courseService.addProgress(this.sectionId)])
                : this.courseService.addProgress(this.sectionId);
    }
}